'use client';

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";


interface OptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  optionData: any;
}

export function OptionDetailsModal({ isOpen, onClose, optionData }: OptionDetailsModalProps) {
  if (!optionData) return null;

  const downloadPDF = () => {
    // Implement PDF download functionality
    console.log("Downloading PDF for", optionData.optionNo);
  };

  const hasShipTestResults = Boolean(
    optionData.testResults && Object.values(optionData.testResults).some((value) => value !== undefined && value !== null && value !== '')
  );
  const hasFtlResults = Boolean(
    optionData.ftl && (
      Object.values(optionData.ftl.preShip || {}).some((value) => value !== undefined && value !== null && value !== '') ||
      Object.values(optionData.ftl.postShip || {}).some((value) => value !== undefined && value !== null && value !== '')
    )
  );
  const shouldShowShipTestResults = optionData.sourceType === 'physical' && (hasShipTestResults || hasFtlResults);
  const shouldShowPalletImage = optionData.sourceType !== 'physical' && Boolean(optionData.palletImage);
  const hasTertiaryDetails = Boolean(
    optionData.tertiaryPackageType || optionData.tertiaryLength || optionData.tertiaryWidth || optionData.tertiaryHeight
  );
  const physicalDetails = [
    ['Pallet Variant', optionData.palletVariant],
    ['Number Of Pallets', optionData.numberOfPallets],
    ['Overall Decision', optionData.overallDecision],
  ].filter(([, value]) => value !== undefined && value !== null && value !== '');

  const formatModalValue = (value: any) => (
    value === undefined || value === null || value === '' ? 'N/A' : value
  );
  const formatFtlValues = (values: any = {}) => [values.top, values.middle, values.bottom]
    .map(formatModalValue)
    .join(':');
  const titleSuffix = optionData.sourceType === 'physical'
    ? 'Physical Ship Test Data'
    : optionData.sourceType === 'specification'
      ? 'Specification Data'
      : 'Option Data';
  const shouldShowAttachments = optionData.sourceType !== 'physical';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>{optionData.optionNo} {titleSuffix}</DialogTitle>
          <div className="flex items-center gap-2">
            {shouldShowPalletImage && (
            <a href={optionData.palletImage} download target="_blank">

              <Button variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download Image
              </Button>
            </a>
            )}
            <DialogClose asChild>
              <Button variant="ghost" size="icon">
                {/* <X className="h-4 w-4" /> */}
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4" id="PopupPDF">
          <div className="space-y-6">
            <div className="space-y-2">
              <h4 className="font-semibold text-lg">Project Details-</h4>
              <ul className="space-y-1 list-disc pl-5 text-sm">
                <li>Option ID / Spec Number - {optionData.optionNo}</li>
                <li>Product Description - {optionData.product}</li>
                <li>Brand - {optionData.brand || 'N/A'}</li>
                <li>Category - {optionData.category || 'N/A'}</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-lg">Primary Details-</h4>
              <ul className="space-y-1 list-disc pl-5 text-sm">
                <li>Package Material - {optionData.packageMaterial || 'N/A'}</li>
                <li>Volume (ml) - {optionData.volume || 'N/A'}</li>
                <li>Filled Weight (g) - {optionData.filledWeight || 'N/A'}</li>
                <li>Diameter (in) - {optionData.diameter || 'N/A'}</li>
                <li>Height (in) - {optionData.height || 'N/A'}</li>
                <li>Bottle Design - {optionData.bottleDesign || 'N/A'}</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-lg">Secondary Package & Tertiary Details-</h4>
              <ul className="space-y-1 list-disc pl-5 text-sm">
                <li>Items / Case - {optionData.itemsCase || 'N/A'}</li>
                <li>Case Length (in) - {optionData.caseLength || 'N/A'}</li>
                <li>Case Width (in) - {optionData.caseWidth || 'N/A'}</li>
                <li>Case Height (in) - {optionData.caseHeight || 'N/A'}</li>
                {hasTertiaryDetails && (
                  <>
                    <li>Tertiary Package Type - {optionData.tertiaryPackageType || 'N/A'}</li>
                    <li>Tertiary Length (in) - {optionData.tertiaryLength || 'N/A'}</li>
                    <li>Tertiary Width (in) - {optionData.tertiaryWidth || 'N/A'}</li>
                    <li>Tertiary Height (in) - {optionData.tertiaryHeight || 'N/A'}</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <h4 className="font-semibold text-lg">Pallet Details-</h4>
              <ul className="space-y-1 list-disc pl-5 text-sm">
                <li>Pallet Dimension (inches) - {optionData.palletTypes || 'N/A'}</li>
                <li>Pallet Material - {optionData.palletMaterial || 'N/A'}</li>
                <li>Unit (inches) L:W:H - {optionData.unitLoadLength || 'N/A'} : {optionData.unitLoadWidth || 'N/A'} : {optionData.unitLoadHeight || 'N/A'}</li>
                <li>Cases per Layer - {optionData.casesPerLayer || 'N/A'}</li>
                <li>Layers per Pallet - {optionData.layersPerPallet || 'N/A'}</li>
                <li>Items / Pallet - {optionData.itemsPallet || 'N/A'}</li>
                <li>Pallet Pattern - {optionData.palletPatternStack || 'N/A'}</li>
              </ul>
            </div>

            {optionData.sourceType === 'physical' && physicalDetails.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-lg">Physical Test Details</h4>
                <ul className="space-y-1 list-disc pl-5 text-sm">
                  {physicalDetails.map(([label, value]) => (
                    <li key={label}>{label} - {formatModalValue(value)}</li>
                  ))}
                </ul>
              </div>
            )}

            {shouldShowShipTestResults && (
              <div className="space-y-2">
                <h4 className="font-semibold text-lg">Ship Test Results</h4>
                <ul className="space-y-1 list-disc pl-5 text-sm">
                  <li>Preship Lean (inches): {formatModalValue(optionData.testResults?.preShipLean)}</li>
                  <li>Preship FTL (T:M:B): {formatFtlValues(optionData.ftl?.preShip)}</li>
                  <li>Postship Lean (inches): {formatModalValue(optionData.testResults?.postShipLean)}</li>
                  <li>Postship FTL (T:M:B): {formatFtlValues(optionData.ftl?.postShip)}</li>
                  <li>Postship Walk (inches): {formatModalValue(optionData.testResults?.postShipWalk)}</li>
                  <li>Poststack Lean (inches): {formatModalValue(optionData.testResults?.postStackLean)}</li>
                  <li>Poststack Compression (inches): {formatModalValue(optionData.testResults?.postStackCompressionRebound)}</li>
                </ul>
              </div>
            )}

            {shouldShowPalletImage && (
              <div className="mt-4">
                <h4 className="font-semibold text-lg mb-2">Pallet Image</h4>
                <div className="border rounded-md overflow-hidden">
                  <img
                    src={optionData.palletImage}
                    alt="Pallet Image"
                    width={300}
                    height={300}
                    className="object-contain"
                  />
                </div>
              </div>
            )}

            {shouldShowAttachments && (
            <div className="space-y-2 mt-4">
              <h4 className="font-semibold text-lg">Attachments</h4>

              <div>
                <h5 className="font-medium">Primary files-</h5>
                {optionData.primaryFiles?.length > 0 ? (
                  <ul className="list-disc pl-5">
                    {optionData.primaryFiles.map((file: any, i: number) => (
                      <li className='text-sm ' key={i}>
                        {file.filename}
                        <a href={file.url} download target="_blank" className="ml-2 text-blue-600 font-bold text-blue underline">
                          Download
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No primary files available</p>
                )}
              </div>

              <div>
                <h5 className="font-medium">Secondary files-</h5>
                {optionData.secondaryFiles?.length > 0 ? (
                  <ul className="list-disc pl-5">
                    {optionData.secondaryFiles.map((file: any, i: number) => (
                      <li className='text-sm ' key={i}>
                        {file.filename}
                        <a href={file.url} download target="_blank" className="ml-2 text-blue-600 font-bold text-blue underline">
                          Download
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No secondary files available</p>
                )}
              </div>

              <div>
                <h5 className="font-medium">Pallet/Unit load files-</h5>
                {optionData.unitFiles?.length > 0 ? (
                  <ul className="list-disc pl-5">
                    {optionData.unitFiles.map((file: any, i: number) => (
                      <li className=' text-sm w-full' key={i}>
                        {file.filename}
                        <a href={file.url} download target="_blank" className="ml-2 text-blue-600 font-bold text-blue underline">
                          Download
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No unit load files available</p>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 
